import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import type { ApiResponse, CompanyRole, Property } from "@/types";

/**
 * Derives the current companyId, propertyId, and filtered properties
 * from the auth and UI stores.
 *
 * The persisted currentCompanyId is validated against the user's memberships —
 * a stale id (removed from the company, company deleted) falls back to the
 * first company instead of 403ing every request. System admins may open
 * non-member companies via the explicit adminJumpCompanyId state; their
 * properties are fetched (they're absent from the auth store).
 */
export function useProperty() {
  const companies = useAuthStore((s) => s.companies);
  const storeProperties = useAuthStore((s) => s.properties);
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin);
  const currentCompanyId = useUiStore((s) => s.currentCompanyId);
  const currentPropertyId = useUiStore((s) => s.currentPropertyId);
  const adminJumpCompanyId = useUiStore((s) => s.adminJumpCompanyId);
  const setAdminJumpCompanyId = useUiStore((s) => s.setAdminJumpCompanyId);

  const isMember = (id: string | null): boolean =>
    !!id && companies.some((c) => c.id === id);

  const adminJumpActive =
    isSystemAdmin && !!adminJumpCompanyId && !isMember(adminJumpCompanyId);

  let companyId: string;
  if (adminJumpActive) {
    companyId = adminJumpCompanyId;
  } else if (isMember(currentCompanyId)) {
    companyId = currentCompanyId as string;
  } else {
    companyId = companies[0]?.id || "";
  }

  // Admin-jumped companies aren't in the auth store — fetch their properties.
  const adminProperties = useQuery({
    queryKey: queryKeys.properties.list(companyId),
    queryFn: () =>
      api.get<ApiResponse<Property[]>>(`/companies/${companyId}/properties`),
    enabled: adminJumpActive && !!companyId,
  });

  // A jump target that no longer resolves (deleted company) clears itself.
  useEffect(() => {
    if (
      adminJumpActive &&
      adminProperties.error instanceof ApiError &&
      adminProperties.error.status === 404
    ) {
      setAdminJumpCompanyId(null);
    }
  }, [adminJumpActive, adminProperties.error, setAdminJumpCompanyId]);

  const adminPropertyData = adminProperties.data?.data;
  const companyProperties = useMemo(
    () =>
      adminJumpActive
        ? (adminPropertyData ?? [])
        : storeProperties.filter((p) => p.companyId === companyId),
    [adminJumpActive, adminPropertyData, storeProperties, companyId],
  );

  const propertyId =
    (currentPropertyId &&
    companyProperties.some((p) => p.id === currentPropertyId)
      ? currentPropertyId
      : companyProperties[0]?.id) || "";

  return {
    companyId,
    propertyId,
    properties: companyProperties,
    adminJumpActive,
    adminJumpLoading: adminJumpActive && adminProperties.isLoading,
  };
}

/**
 * The caller's role in the currently selected company. System admins resolve
 * to "owner", matching the API's ResolveTenant bypass.
 */
export function useCurrentRole(): CompanyRole | null {
  const companies = useAuthStore((s) => s.companies);
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin);
  const { companyId } = useProperty();

  if (isSystemAdmin) return "owner";
  return companies.find((c) => c.id === companyId)?.role ?? null;
}
